import { Seo } from "@/components/Seo";
import { ModuleTrialPrompt } from "@/components/ModuleTrialPrompt";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, Globe, Loader2, User, Lock, Crown, Paperclip, X, Search, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail, streamPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Disclaimer } from "@/components/Disclaimer";
import { toast } from "@/components/ui/sonner";
import { VoiceButton } from "@/components/VoiceButton";
import { Markdown } from "@/components/Markdown";
import { ChatHistory } from "@/components/ChatHistory";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी · Hindi" },
  { code: "gu", label: "ગુજરાતી · Gujarati" },
  { code: "mr", label: "मराठी · Marathi" },
  { code: "ta", label: "தமிழ் · Tamil" },
  { code: "te", label: "తెలుగు · Telugu" },
  { code: "bn", label: "বাংলা · Bengali" },
  { code: "kn", label: "ಕನ್ನಡ · Kannada" },
  { code: "pa", label: "ਪੰਜਾਬੀ · Punjabi" },
  { code: "ml", label: "മലയാളം · Malayalam" },
  { code: "ur", label: "اردو · Urdu" },
];

const MODES = [
  { id: "research", label: "Research", icon: Search, desc: "Deep legal research: exact statutes, leading precedents & structured analysis." },
  { id: "consultation", label: "Consultation", icon: MessageSquare, desc: "Practical advice on your situation — your options, risks & concrete next steps." },
];

const QUICK_REPLIES = {
  research: ["Cite the leading judgments", "Explain the governing statute", "Summarise the key legal issues"],
  consultation: ["What are my options?", "What are the risks?", "What should my next steps be?"],
};

export default function JusticeBot() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("en");
  const [mode, setMode] = useState("research");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [limitModal, setLimitModal] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);
  const [attachments, setAttachments] = useState([]); // [{ file, preview, kind }]
  const fileRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const loadSession = async (sid) => {
    try {
      const { data } = await api.get(`/chat/${sid}/messages`);
      setMessages(data.map((m) => ({ role: m.role, content: m.content })));
      setSessionId(sid);
      setLimitModal(null);
    } catch {
      toast.error("Could not load that conversation.");
    }
  };

  const newChat = () => {
    setMessages([]); setSessionId(null); setInput(""); setAttachments([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if ((!content && attachments.length === 0) || loading) return;
    setInput("");
    const sendFiles = attachments;
    setMessages((m) => [...m, {
      role: "user",
      content: content || (sendFiles.length ? `Analyse ${sendFiles.length > 1 ? "these attachments" : "this attachment"}` : ""),
      attachments: sendFiles.map((a) => ({ preview: a.preview, kind: a.kind, name: a.file.name })),
    }]);
    setLoading(true);
    setAttachments([]);
    if (fileRef.current) fileRef.current.value = "";

    // Attachments (images/PDFs/videos) → non-streaming multimodal path
    if (sendFiles.length > 0) {
      try {
        const fd = new FormData();
        fd.append("message", content);
        if (sessionId) fd.append("session_id", sessionId);
        fd.append("language", language);
        fd.append("mode", mode);
        sendFiles.forEach((a) => fd.append("files", a.file));
        const { data } = await api.post("/chat", fd);
        setSessionId(data.session_id);
        setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        setHistoryKey((k) => k + 1);
      } catch (e) {
        handleSendError(e);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Text-only → live token streaming
    setMessages((m) => [...m, { role: "assistant", content: "", streaming: true }]);
    try {
      await streamPost("/chat/stream", { message: content, session_id: sessionId || "", language, mode }, (evt) => {
        if (evt.type === "meta") {
          setSessionId(evt.session_id);
        } else if (evt.type === "delta") {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, content: last.content + evt.text };
            return copy;
          });
        } else if (evt.type === "error") {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: evt.message, error: true };
            return copy;
          });
        }
      });
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant") copy[copy.length - 1] = { ...last, streaming: false };
        return copy;
      });
      setHistoryKey((k) => k + 1);
    } catch (e) {
      // Remove the empty assistant placeholder before surfacing the error/limit
      setMessages((m) => (m[m.length - 1]?.role === "assistant" && !m[m.length - 1]?.content ? m.slice(0, -1) : m));
      if (e.response?.status === 402) {
        const reason = e.response.data?.detail?.reason;
        setLimitModal(reason === "free_limit" ? "free" : "guest");
      } else {
        const msg = handleSendError(e, true);
        setMessages((m) => [...m, { role: "assistant", content: msg, error: true }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendError = (e, returnOnly = false) => {
    if (e.response?.status === 402) {
      const reason = e.response.data?.detail?.reason;
      setLimitModal(reason === "free_limit" ? "free" : "guest");
      return null;
    }
    const status = e.response?.status;
    let msg;
    if (!e.response) {
      msg = "Network issue reaching JusticeVerse AI. Please check your connection and try again.";
    } else if (status >= 500 && status !== 504) {
      msg = "JusticeVerse AI is busy right now — please try again in a moment.";
    } else {
      msg = formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.";
    }
    toast.error(msg);
    if (returnOnly) return msg;
    setMessages((m) => [...m, { role: "assistant", content: msg, error: true }]);
    return msg;
  };

  const onPickFile = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setAttachments((prev) => {
      const next = [...prev];
      for (const f of picked) {
        if (f.size > 20_000_000) { toast.error(`"${f.name}" is too large (max 20 MB).`); continue; }
        if (next.length >= 10) { toast.error("You can attach up to 10 files."); break; }
        const isPdf = f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf");
        const isVideo = f.type.startsWith("video/");
        next.push({
          file: f,
          preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
          kind: isPdf ? "pdf" : isVideo ? "video" : "image",
        });
      }
      return next;
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAttachment = (idx) => setAttachments((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 min-h-[80vh] flex flex-col" data-testid="justicebot-page">
      <ModuleTrialPrompt />
      <Seo title="JusticeVerse AI — AI Legal Research Assistant for Indian Law" description="Ask JusticeVerse AI any question on Indian law and get accurate, multilingual answers with verified citations. AI legal research across BNS, BNSS, IPC, CrPC, CPC and landmark judgments." />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-8">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Module 01
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">JusticeVerse AI</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 glass-card rounded-full p-1" data-testid="mode-selector">
            {MODES.map((mm) => {
              const Icon = mm.icon;
              return (
                <button
                  key={mm.id}
                  onClick={() => setMode(mm.id)}
                  data-testid={`mode-${mm.id}`}
                  className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-all ${mode === mm.id ? "bg-gold text-black" : "text-inkmuted hover:text-ink"}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {mm.label}
                </button>
              );
            })}
          </div>
          <ChatHistory
            user={user}
            listUrl="/chat/sessions"
            normalize={(d) => d.map((s) => ({ id: s.session_id, title: s.title, created_at: s.created_at }))}
            onSelect={loadSession}
            onNew={newChat}
            deleteUrl={(id) => `/chat/${id}`}
            deleteAllUrl="/chat/sessions"
            activeId={sessionId}
            reloadKey={historyKey}
            label="JusticeVerse History"
          />
          <div className="flex items-center gap-2 glass-card rounded-full pl-4 pr-2 py-1.5" data-testid="language-selector">
            <Globe className="w-4 h-4 text-inkfaint" />
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-[170px] bg-transparent border-0 text-sm focus:ring-0 text-ink" data-testid="language-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-panel border-hairline text-ink shadow-xl max-h-72">
                {LANGS.map((l) => (
                  <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`} className="text-sm focus:bg-gold/15 focus:text-ink data-[state=checked]:text-gold">
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="glass-card rounded-3xl flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-[42vh]" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center py-12">
              <div className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-gold" />
              </div>
              <p className="font-serif text-2xl text-ink">Ask anything about Indian law</p>
              <p className="text-sm text-inkfaint mt-2 max-w-md">
                Choose <span className="text-gold">Research</span> or <span className="text-gold">Consultation</span> below, then ask your question — in 11 languages.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8 w-full max-w-xl">
                {MODES.map((mm) => {
                  const Icon = mm.icon;
                  const active = mode === mm.id;
                  return (
                    <button
                      key={mm.id}
                      onClick={() => setMode(mm.id)}
                      data-testid={`mode-card-${mm.id}`}
                      className={`flex-1 text-left rounded-2xl border p-5 transition-all ${active ? "border-gold bg-gold/10" : "border-hairline hover:border-gold/50 bg-surface"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-gold text-black" : "border border-hairline text-gold"}`}>
                          <Icon className="w-4 h-4" />
                        </span>
                        <span className="font-serif text-lg text-ink">{mm.label}</span>
                        {active && <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-gold">Active</span>}
                      </div>
                      <p className="text-xs text-inkmuted mt-3 leading-relaxed">{mm.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              {m.role === "assistant" && (
                <div className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-gold" />
                </div>
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-5 py-4 text-sm leading-relaxed ${
                  m.role === "assistant" && !m.error ? "" : "whitespace-pre-wrap"
                } ${
                  m.role === "user"
                    ? "bg-surface2 border border-hairline text-ink"
                    : m.error
                    ? "bg-red-500/10 border border-red-500/30 text-red-300"
                    : "bg-surface border border-gold/20 text-ink"
                }`}
              >
                {m.attachments && m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {m.attachments.map((a, ai) => (
                      a.kind === "image" && a.preview ? (
                        <img key={ai} src={a.preview} alt={a.name} className="rounded-lg max-h-28 border border-hairline" />
                      ) : a.kind === "video" && a.preview ? (
                        <video key={ai} src={a.preview} controls className="rounded-lg max-h-28 border border-hairline" />
                      ) : (
                        <div key={ai} className="flex items-center gap-2 bg-surface border border-hairline rounded-lg px-3 py-2">
                          <Paperclip className="w-4 h-4 text-gold" />
                          <span className="text-xs text-inkmuted truncate max-w-[160px]">{a.name}</span>
                        </div>
                      )
                    ))}
                  </div>
                )}
                {m.role === "assistant" && !m.error
                  ? (m.content
                      ? <Markdown>{m.content}</Markdown>
                      : <span className="flex items-center gap-2 text-inkmuted" data-testid="streaming-indicator"><Loader2 className="w-4 h-4 animate-spin text-gold" /> {mode === "consultation" ? "Consulting…" : "Researching…"}</span>)
                  : m.content}
              </div>
              {m.role === "user" && (
                <div className="w-9 h-9 rounded-full bg-btn text-btnink flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}

          {loading && messages[messages.length - 1]?.role === "user" && (
            <div className="flex gap-3 justify-start">
              <div className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-gold" />
              </div>
              <div className="bg-surface border border-gold/20 rounded-2xl px-5 py-4 flex items-center gap-2 text-inkmuted text-sm">
                <Loader2 className="w-4 h-4 animate-spin text-gold" /> {mode === "consultation" ? "Consulting…" : "Researching…"}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-hairline p-4">
          {messages.length > 0 && !loading && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="quick-replies">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint mr-1">Quick reply</span>
              {QUICK_REPLIES[mode].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => send(q)}
                  data-testid="quick-reply-chip"
                  className="rounded-full border border-hairline bg-surface hover:border-gold/50 hover:text-gold text-inkmuted text-xs px-3.5 py-1.5 transition-all"
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {attachments.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="attachment-chips">
              {attachments.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-surface border border-hairline rounded-xl p-1.5 pr-2.5" data-testid="attachment-chip">
                  {a.preview ? (
                    <img src={a.preview} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-surface2 flex items-center justify-center"><Paperclip className="w-4 h-4 text-gold" /></div>
                  )}
                  <span className="text-xs text-inkmuted max-w-[140px] truncate">{a.file.name}</span>
                  <button onClick={() => removeAttachment(idx)} className="text-inkfaint hover:text-red-400" data-testid="remove-attachment-btn"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={fileRef} type="file" multiple accept="image/*,video/*,application/pdf,.pdf" className="hidden" data-testid="chat-file-input" onChange={onPickFile} />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="chat-attach-btn"
              title="Attach images, videos or PDFs"
              className="rounded-full w-11 h-11 flex items-center justify-center text-inkmuted hover:text-gold hover:bg-surface transition-all shrink-0"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <VoiceButton lang={language} onTranscript={(t) => setInput((p) => (p ? p + " " : "") + t)} className="rounded-full w-11 h-11 flex items-center justify-center hover:bg-surface transition-all shrink-0" />
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              data-testid="chat-input"
              rows={1}
              placeholder="Ask JusticeVerse AI, attach photos or PDFs, or tap the mic…"
              className="flex-1 bg-transparent resize-none outline-none text-ink placeholder:text-inkfaint text-sm py-3 px-2 max-h-32"
            />
            <button
              onClick={() => send()}
              disabled={loading || (!input.trim() && attachments.length === 0)}
              data-testid="chat-send-btn"
              className="rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-40 w-11 h-11 flex items-center justify-center transition-all shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <Disclaimer className="mt-5 justify-center" />

      {limitModal && (
        <div className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLimitModal(null)} data-testid="limit-modal">
          <div className="glass-card rounded-3xl max-w-sm w-full p-8 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full border border-gold/40 flex items-center justify-center mx-auto">
              {limitModal === "free" ? <Crown className="w-6 h-6 text-gold" /> : <Lock className="w-6 h-6 text-gold" />}
            </div>
            {limitModal === "guest" ? (
              <>
                <h3 className="font-serif text-2xl text-ink mt-5">You&apos;ve used your free messages</h3>
                <p className="text-sm text-inkmuted mt-2">Guests get 3 free messages. Create a free account to unlock 30 free across all features — or subscribe for unlimited.</p>
                <div className="flex flex-col gap-3 mt-7">
                  <Link to="/register" data-testid="limit-register-btn" className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3 text-sm font-semibold uppercase tracking-wider">Create Free Account</Link>
                  <Link to="/login" data-testid="limit-login-btn" className="rounded-full bg-surface border border-hairline text-ink hover:bg-surface2 px-6 py-3 text-sm font-semibold uppercase tracking-wider">Sign In</Link>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-serif text-2xl text-ink mt-5">Free limit reached</h3>
                <p className="text-sm text-inkmuted mt-2">You&apos;ve used all 30 free messages. Subscribe to continue with unlimited access to all features.</p>
                <div className="flex flex-col gap-3 mt-7">
                  <Link to="/pricing" data-testid="limit-subscribe-btn" className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3 text-sm font-semibold uppercase tracking-wider">View Plans</Link>
                  <button onClick={() => setLimitModal(null)} className="text-inkfaint hover:text-ink text-sm">Maybe later</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
